import { CardVariant } from "./components/Card";

export type Scope = 1 | 2 | 3;

export const unitIdSuffixMap: { [key in Scope]: React.ReactNode } = {
    1: (
        <>
            ft<sup>3</sup>
        </>
    ),
    2: "KWH",
    3: "tons",
};

export const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
];

export const scopeOptions = [
    {
        value: 1,
        label: "Scope 1",
    },
    {
        value: 2,
        label: "Scope 2",
    },
    {
        value: 3,
        label: "Scope 3",
    },
];

export const dataStatusIds = {
    reportVerified: 1,
    reportedUnverified: 2,
    measuredElectricity: 3,
    measuredProduction: 4,
    estimatedFromCompanyRevenue: 5,
    estimatedFromAssetValue: 6,
    estimatedFromSectorATR: 7
};

export const dataStatusOptions = [
    {
        value: dataStatusIds.reportVerified,
        label: "Reported - Verified",
    },
    {
        value: dataStatusIds.reportedUnverified,
        label: "Reported - Unverified",
    },
    {
        value: dataStatusIds.measuredElectricity,
        label: "Measured - Electricity",
    },
    {
        value: dataStatusIds.measuredProduction,
        label: "Measured - Production",
    },
    {
        value: dataStatusIds.estimatedFromAssetValue,
        label: "Estimated - Asset Value",
    },
    {
        value: dataStatusIds.estimatedFromCompanyRevenue,
        label: "Estimated - Asset Revenue",
    },
    {
        value: dataStatusIds.estimatedFromSectorATR,
        label: "Estimated - Sector Asset Turnover Ratio",
    },
];

export const emissionsFactorOptions = [
    { label: "All", value: null },
    {
        label: "Incomplete",
        value: "NotSet",
    },
    {
        label: "Complete",
        value: "Set",
    },
];

export type RagStatus = "red" | "amber" | "green";

export const ragToBgClassMap: { [key in RagStatus]: string } = {
    red: "bg-red-40",
    amber: "bg-rag-amber !text-black",
    green: "bg-soft-green-80 !text-black",
};

export const ragToRgb: { [key in RagStatus]: string } = {
    red: "rgb(186 27 27)",
    amber: "rgb(243 243 153)",
    green: "rgb(181 216 138)",
};

export const ragToCardVariantMap: { [property in RagStatus]: CardVariant } = {
    red: "rag-red",
    amber: "rag-amber",
    green: "rag-green",
};

export type ConfidenceName = "Poor" | "Fair" | "High";

export const confidenceNameToRagMap: {
    [property in ConfidenceName]: RagStatus;
} = {
    Poor: "red",
    Fair: "amber",
    High: "green",
};

export type DataStatusName =
    | "Omitted"
    | "Estimated"
    | "MeasuredSecondary"
    | "MeasuredPrimary";

export const dataStatusNameToColourMap: {
    [property in DataStatusName]: Colour;
} = {
    Omitted: "red",
    Estimated: "orange",
    MeasuredSecondary: "amber",
    MeasuredPrimary: "green",
};

export type Colour = "red" | "orange" | "amber" | "green" | "lightGreen";



export const colourToBgClassMap: { [key in Colour]: string } = {
    red: "bg-red-40",
    orange: "bg-orange-300 !text-black",
    amber: "bg-rag-amber !text-black",
    green: "bg-soft-green-80 !text-black",
    lightGreen: "bg-light-green !text-black"
};

export const colourToRgb: { [key in Colour]: string } = {
    red: "rgb(186 27 27)",
    orange: "rgb(253 186 116)",
    amber: "rgb(243 243 153)",
    lightGreen: "rgb(144, 238, 144)",
    green: "rgb(181 216 138)",
};

export const PCAFScoreToRgb: { [score in 1 | 2 | 3 | 4 | 5]: string } = {
    1: colourToRgb["green"],
    2: colourToRgb["lightGreen"],
    3: colourToRgb["amber"],
    4: colourToRgb["orange"],
    5: colourToRgb["red"],
};

export const scopeToBgClassMap: { [key in Scope]: string } = {
    1: "bg-[var(--scope-1)]",
    2: "bg-[var(--scope-2)]",
    3: "bg-[var(--scope-3)]",
};

export type DateFilterValue =
    | "this-month"
    | "this-quarter"
    | "this-year"
    | "last-month"
    | "last-quarter"
    | "last-year"
    | "two-years-ago"
    | "three-years-ago"
    | "four-years-ago"
    | "five-years-ago"
    | "all";

export const dateRangeFilterDropdownOptions: {
    label: string;
    value: DateFilterValue;
}[] = [
    {
        label: "This month",
        value: "this-month",
    },
    {
        label: "This quarter",
        value: "this-quarter",
    },
    {
        label: "This year",
        value: "this-year",
    },
    {
        label: "Last month",
        value: "last-month",
    },
    {
        label: "Last quarter",
        value: "last-quarter",
    },
    {
        label: "Last year",
        value: "last-year",
    },
    {
        label: "2 years ago",
        value: "two-years-ago",
    },
    {
        label: "3 years ago",
        value: "three-years-ago",
    },
    {
        label: "4 years ago",
        value: "four-years-ago",
    },
    {
        label: "5 years ago",
        value: "five-years-ago",
    },
    {
        label: "All",
        value: "all",
    },
];

export const reportTypeOptions = [
    {
        value: "SASB",
        label: "SASB (Sustainability Accounting Standards Board)",
    },
    {
        value: "GRI",
        label: "GRI (Global Reporting Initiative)",
    },
    {
        value: "ISO 14064",
        label: "ISO 14064",
    },
    {
        value: "API",
        label: "API (American Petroleum Institute)",
    },
    {
        value: "EU ETS",
        label: "EU ETS (Energy Trading Scheme)",
    },
    {
        value: "UK ETS",
        label: "UK ETS (Energy Trading Scheme)",
    },
    {
        value: "Custom report",
        label: "Custom report",
    },
];

export const confidenceOptions = [
    {
        value: 3,
        label: "Poor",
    },
    {
        value: 2,
        label: "Fair",
    },
    {
        value: 1,
        label: "High",
    },
];

export const isMaterial = 1;
export const notMaterial = 2;

export const iso14064Labels = {
    scopingBoundaries: "Boundaries",
    scopingEmissionsScopes: "Emissions scopes",
    scopingEmissionsSources: "Emissions sources",
    scopingEmissionsTypes: "Emissions types",
    scopingActivityData: "Activity\ndata",
    scopingMateriality: "Materiality",
    scopingStandards: "Standards",
    dataCertificates: "Certificates",
    dataUncertainty: "Uncertainty",
    dataErrors: "Errors",
    dataEmissionsFactors: "Emissions factors",
    dataConversionFactors: "Conversion factors",
    dataEquations: "Equations",
    dataEstimationMethods: "Estimation methods",
    dataReportingShare: "Reporting share",
    dataReportingPeriod: "Reporting period",
    qualityControlAssumptions: "Assumptions",
    qualityControlQaProcedure: "QA procedures",
    qualityControlCalculationMethods: "Calculation methods",
    qualityControlRolesAndResponsibilities: "Roles & Responsibilities",
    qualityControlQcCheck: "QC check",
    qualityControlMaterialityThreshold: "Materiality threshold",
};
